import { Skeleton } from "@hakistudio/hakiui";
import { TASK_STATUSES, TASK_STATUS_LABELS, type TaskStatus } from "@sebas/core";
import { ListTodo, Plus } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { TaskCard } from "@/components/tasks/TaskCard";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { cn } from "@/lib/cn";
import { useMoveTask, useTasks, type Task } from "@/lib/tasks";

type View = "board" | "list";

export function TasksPage() {
  const [view, setView] = useState<View>("board");
  const [editing, setEditing] = useState<Task | null>(null);
  const [creatingIn, setCreatingIn] = useState<TaskStatus | null>(null);
  /**
   * The card being dragged.
   *
   * Held in a ref as well as state: the drop handler closes over `dragging`, and
   * if a drop lands before React has re-rendered from the dragstart it would read
   * a stale `null` and silently do nothing. The ref is the source of truth for
   * the logic; the state exists only to fade the card being dragged.
   */
  const draggingRef = useRef<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  const beginDrag = (id: string) => {
    draggingRef.current = id;
    setDragging(id);
  };
  const endDrag = () => {
    draggingRef.current = null;
    setDragging(null);
  };

  const { data, isPending } = useTasks();
  const move = useMoveTask();

  const columns = useMemo(() => {
    const byStatus = new Map<TaskStatus, Task[]>(TASK_STATUSES.map((s) => [s, []]));
    for (const t of data?.tasks ?? []) byStatus.get(t.status)?.push(t);
    for (const list of byStatus.values()) list.sort((a, b) => a.position - b.position);
    return byStatus;
  }, [data]);

  const total = data?.tasks.length ?? 0;
  const doneCount = columns.get("done")?.length ?? 0;

  /**
   * Drop handler.
   *
   * `beforeId` is the card the dragged one lands above; the server derives the
   * sort key from the pair so concurrent drags cannot collide on a number.
   */
  const drop = (status: TaskStatus, beforeId: string | null) => {
    const id = draggingRef.current;
    if (!id || id === beforeId) return;

    const column = (columns.get(status) ?? []).filter((t) => t.id !== id);
    const index = beforeId ? column.findIndex((t) => t.id === beforeId) : column.length;
    const afterId = index > 0 ? column[index - 1]!.id : null;

    move.mutate({ id, status, afterId, beforeId });
    endDrag();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-5xl font-semibold lg:text-6xl">Tasks</h1>
        {total > 0 ? (
          <p className="text-sm text-ink-muted">
            {doneCount} of {total} done
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["board", "list"] as View[]).map((v) => (
          <Chip key={v} active={view === v} onClick={() => setView(v)}>
            {v === "board" ? "Board" : "List"}
          </Chip>
        ))}
        <Button size="sm" className="ml-auto" onClick={() => setCreatingIn("todo")}>
          <Plus className="h-4 w-4" /> New task
        </Button>
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="12rem" radius="lg" />
          ))}
        </div>
      ) : total === 0 ? (
        <div className="rounded-card border border-dashed border-line px-6 py-14 text-center">
          <ListTodo className="mx-auto h-6 w-6 text-ink-faint" />
          <p className="font-display mt-3 text-2xl font-semibold">Nothing on the list</p>
          <p className="mt-1 text-sm text-ink-muted">Add a task to get started.</p>
        </div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {TASK_STATUSES.map((status) => {
            const items = columns.get(status) ?? [];
            return (
              <section
                key={status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => drop(status, null)}
                className={cn(
                  "flex min-h-40 flex-col gap-2 rounded-card bg-sunken/60 p-3",
                  dragging && "outline-2 outline-dashed outline-line",
                )}
              >
                <header className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-semibold text-ink">{TASK_STATUS_LABELS[status]}</h2>
                  <span className="text-xs text-ink-faint">{items.length}</span>
                </header>

                {items.map((task) => (
                  <div
                    key={task.id}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      // Stop the column handler from also firing and appending.
                      e.stopPropagation();
                      drop(status, task.id);
                    }}
                  >
                    <TaskCard
                      task={task}
                      draggable
                      dragging={dragging === task.id}
                      onDragStart={() => beginDrag(task.id)}
                      onDragEnd={endDrag}
                      onOpen={() => setEditing(task)}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => setCreatingIn(status)}
                  className="flex items-center gap-1.5 rounded-2xl px-3 py-2 text-left text-xs text-ink-faint hover:bg-sunken hover:text-ink-muted"
                >
                  <Plus className="h-3.5 w-3.5" /> Add task
                </button>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {TASK_STATUSES.map((status) => {
            const items = columns.get(status) ?? [];
            if (items.length === 0) return null;
            return (
              <section key={status} className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold text-ink-muted">
                  {TASK_STATUS_LABELS[status]} · {items.length}
                </h2>
                {items.map((task) => (
                  <TaskCard key={task.id} task={task} onOpen={() => setEditing(task)} />
                ))}
              </section>
            );
          })}
        </div>
      )}

      {editing || creatingIn ? (
        <TaskDialog
          task={editing}
          defaultStatus={creatingIn ?? "todo"}
          onClose={() => {
            setEditing(null);
            setCreatingIn(null);
          }}
        />
      ) : null}
    </div>
  );
}
